import { useEffect, useState } from "react";
import { listAuditLog, type AuditEntry } from "@/lib/api";
import { formatDate, formatRelative } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, Badge, type ColumnDef } from "@/components/DataTable";

export function AuditLogPage() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [actionFilter, setActionFilter] = useState("");

  const load = async (reset = false) => {
    setLoading(true);
    try {
      const params: any = { limit: 50 };
      if (!reset && cursor) params.cursor = cursor;
      if (actionFilter) params.action = actionFilter;
      const r = await listAuditLog(params);
      setEntries(reset ? r.data : [...entries, ...r.data]);
      setCursor(r.next_cursor ?? null);
      setHasMore(r.has_more);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCursor(null);
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionFilter]);

  const columns: ColumnDef<AuditEntry>[] = [
    {
      key: "date",
      header: "Date",
      minWidth: 110,
      cell: (e) => <span className="text-xs text-[#7e8299]" title={formatDate(e.created_at)}>{formatRelative(e.created_at)}</span>,
    },
    {
      key: "user",
      header: "Utilisateur",
      minWidth: 200,
      cell: (e) => <span className="font-mono text-xs">{e.user_id ?? "system"}</span>,
    },
    {
      key: "action",
      header: "Action",
      minWidth: 160,
      cell: (e) => <Badge color={actionColor(e.action)}>{e.action}</Badge>,
    },
    {
      key: "resource",
      header: "Ressource",
      minWidth: 240,
      wrap: true,
      cell: (e) => (
        <div>
          <div className="text-xs text-[#7e8299]">{e.resource_type ?? "—"}</div>
          <div className="font-mono text-xs">{e.resource_id ?? ""}</div>
        </div>
      ),
    },
    {
      key: "merchant",
      header: "Marchand",
      minWidth: 240,
      cell: (e) => <span className="font-mono text-xs text-[#7e8299]">{e.merchant_id ?? "—"}</span>,
    },
    {
      key: "ip",
      header: "IP",
      minWidth: 120,
      cell: (e) => <span className="font-mono text-xs text-[#7e8299]">{e.ip_address ?? "—"}</span>,
    },
  ];

  const filters = (
    <input
      type="text"
      value={actionFilter}
      onChange={(e) => setActionFilter(e.target.value)}
      placeholder="Filtrer par action (ex: merchant.created)"
      className="form-input py-2 px-3 text-sm w-72"
    />
  );

  return (
    <div>
      <PageHeader
        title="Audit log"
        breadcrumbs={[{ label: "Administration" }, { label: "Audit log" }]}
      />

      <DataTable<AuditEntry>
        title="Historique des actions sensibles"
        subtitle="Création/modification de marchands, rotations, suspensions…"
        columns={columns}
        data={entries}
        rowKey={(e) => e.id}
        loading={loading && entries.length === 0}
        filtersSlot={filters}
        loadMore={hasMore ? { hasMore, onLoadMore: () => load(false), loading } : undefined}
      />
    </div>
  );
}

function actionColor(action: string): "primary" | "success" | "danger" | "warning" | "light" {
  if (action.includes("create") || action.includes("activate")) return "success";
  if (action.includes("delete") || action.includes("suspend")) return "danger";
  if (action.includes("rotate") || action.includes("update")) return "warning";
  if (action.includes("login")) return "primary";
  return "light";
}
